import type { IvrGraph, IvrState, IvrTransition } from '../api/payersApi';

// Display-only derivations over an already-synthesized IvrGraph — mirrors
// backend charting/renderer.ts's action-phrase logic so the states list here
// reads consistently with the rendered instructions shown alongside it.

export function hasSubscriberDataPath(graph: IvrGraph): boolean {
  return graph.states.some((s) => s.outcome === 'subscriber_data_required');
}

// 'reached' = a run got to a human/queue. 'unavailable' = the payer has
// explicitly stated no representative exists for this call type — a
// confirmed fact, not a gap. 'unresolved' = genuinely don't know yet, more
// charting may help. These three read very differently to a user and must
// not be collapsed into one boolean.
export type RepresentativeStatus = 'reached' | 'unavailable' | 'unresolved';

export function representativeStatus(graph: IvrGraph): RepresentativeStatus {
  if (graph.states.some((s) => s.outcome === 'human_answered' || s.outcome === 'queue_reached')) {
    return 'reached';
  }
  if (graph.states.some((s) => s.outcome === 'representative_unavailable')) {
    return 'unavailable';
  }
  return 'unresolved';
}

export type StateKindBadge =
  | 'gate'
  | 'reached'
  | 'unavailable'
  | 'announcement'
  | 'entry'
  | 'menu'
  | 'terminal';

export function stateKindBadge(state: IvrState): { label: string; variant: StateKindBadge } {
  if (state.outcome === 'subscriber_data_required')
    return { label: 'MEMBER-ID GATE', variant: 'gate' };
  if (state.outcome === 'human_answered' || state.outcome === 'queue_reached') {
    return { label: 'REACHED', variant: 'reached' };
  }
  if (state.outcome === 'representative_unavailable') {
    return { label: 'NO REP', variant: 'unavailable' };
  }
  if (state.kind === 'announcement') return { label: 'ANNOUNCEMENT', variant: 'announcement' };
  if (state.kind === 'data_entry') return { label: 'DATA ENTRY', variant: 'entry' };
  if (state.kind === 'menu') return { label: 'MENU', variant: 'menu' };
  return { label: 'TERMINAL', variant: 'terminal' };
}

function actionPhrase(t: IvrTransition): string {
  switch (t.action) {
    case 'press':
      return `press ${t.value ?? ''}`.trim();
    case 'key_variable':
      return `key {{${t.value ?? ''}}}`;
    case 'key_literal':
      return `key ${t.value ?? ''}`.trim();
    case 'say':
      return `say "${t.value ?? ''}"`;
    case 'listen_silent':
      return 'stay silent — announcement, not a menu';
    case 'confirm_then_press':
      return `confirm, then press ${t.value ?? ''}`.trim();
    default:
      return '';
  }
}

// The one confirmed transition leaving this state, if any — an unconfirmed
// (known-from-menu-options-but-never-taken) branch is never shown as "the"
// action here, matching the renderer's own confirmed-only fast path.
export function confirmedActionFrom(graph: IvrGraph, stateId: string): string | null {
  const transition = graph.transitions.find((t) => t.fromState === stateId && t.confirmed);
  if (!transition) return null;
  let phrase = actionPhrase(transition);
  if (transition.terminator === '#') phrase += ', then pound';
  return phrase;
}
